import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    type UpdateTtbPaymentModuleMutation,
    type UpdateTtbPaymentModuleMutationVariables,
    UpdateTtbPaymentModuleDocument,
} from '../../../../api/mutations/updateTtbPaymentModule';
import { type TtbPaymentModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = TtbPaymentModuleSettings;

type updatePayGatePaymentModuleFormProps = {
    moduleId: string;
    initialValues: FormValues;
    onComplete: () => void;
};

const loadingMessageKey = 'update-ttb-payment-module-loading';

const UpdateTtbPaymentModuleForm = ({ moduleId, initialValues, onComplete }: updatePayGatePaymentModuleFormProps) => {
    const { t } = useTranslation(['ttbPaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('ttbPaymentModuleDetails:paymentModuleDrawer.messages.submitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateTtbPaymentModuleMutation, UpdateTtbPaymentModuleMutationVariables>({
                    mutation: UpdateTtbPaymentModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('ttbPaymentModuleDetails:paymentModuleDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateTtbPaymentModule"
                    layout="vertical"
                    name="updateTtbPaymentModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentModuleDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateTtbPaymentModuleForm;
